import React from "react";
import {
  PRELIQUIDATION_STATUS,
  PRELIQUIDATION_STATUS_NAMES,
  PreliquidationStatusTags,
} from "utils/constants";
import "./tablePreliquidation.scss";
import { TablePreliquidationProps } from "./types";
import { AppDispatch, RootState } from "store";
import {
  actions as preliActions,
  allPreliquidationsSelected,
  preliquidationSelector,
} from "reducers/preliquidation";
import { PreliquidationItem } from "sagas/types/preliquidation";
import { ConnectedProps, connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { formatDDMMYYYY } from "utils/date";
import "./i18n";
import { useTranslation } from "react-i18next";
import { HeaderType, Table } from "@pickit/pickit-components";
import useTableConfig from "hooks/useTableConfig";

const TablePreliquidation = ({
  items,
  preliquidationsSelected,
  toggleItem,
  isAllSelected,
  toggleAll,
  getMorePreliquidations,
  seeMore,
  filters,
  filtersExtraSeeMore,
  testId,
  collapse,
}: TablePreliquidationProps & ConnectorProps) => {
  const { t } = useTranslation("tablePresettlements");
  const history = useHistory();

  const isSelected = (itemId: number) =>
    preliquidationsSelected?.map((p) => p.id).includes(itemId);
  const { fontSize, rowCount } = useTableConfig(collapse);

  const headers: HeaderType<PreliquidationItem>[] = [
    {
      key: "id",
      label: t("preliNumber"),
      testId: "id",
    },
    {
      key: "pickerId",
      label: t("pickerId"),
      testId: "picker-id",
    },
    {
      key: "fiscalNumber",
      label: t("if"),
      testId: "fiscal-number",
      textConfig: {
        formatter: (value: string) => value || "-",
      },
    },
    {
      key: "generatedAt",
      label: t("dateOfIssue"),
      textConfig: {
        formatter: (date: string) => date && formatDDMMYYYY(date),
      },
      testId: "date",
    },
    {
      key: "status",
      label: t("status"),
      testId: "status",
      textConfig: {
        formatter: (status: string) =>
          t(
            `${PRELIQUIDATION_STATUS_NAMES[status as PreliquidationStatusTags]}`
          ),
      },
    },
    {
      key: "total",
      label: t("total"),
      testId: "total",
      textConfig: {
        formatter: (total: string) => `$${total}`,
      },
      col: 2,
    },
    {
      type: "checkbox",
      col: 1,
      align: "center",
      testId: "select",
      showCondition: (item: PreliquidationItem) =>
        item?.status === PRELIQUIDATION_STATUS.APPROVED || item?.status === PRELIQUIDATION_STATUS.PENDING_ACCOUNTING,
      checkboxConfig: {
        isChecked: (item) => !!isSelected(item.id),
        onChangeCheck: (item) => toggleItem && toggleItem(item),
        selectAll: () => toggleAll && toggleAll(),
        allSelected: () => !!isAllSelected,
      },
    },
  ];

  return (
    <Table<PreliquidationItem>
      headers={headers}
      data={items}
      testId={testId}
      pagination="view-more-button"
      hasMoreRows={seeMore}
      loadMoreRows={() =>
        getMorePreliquidations({ ...filters, ...filtersExtraSeeMore })
      }
      rowCount={rowCount}
      onClickRow={(preli) => history.push(`presettlements/detail/${preli.id}?tab=preliquidation`)}
      fontSize={fontSize}
      cols={20}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  preliquidationsSelected:
    preliquidationSelector(state).preliquidationsSelected,
  isAllSelected: allPreliquidationsSelected(state),
  filters: preliquidationSelector(state).filters,
  filtersExtraSeeMore: preliquidationSelector(state).filtersExtraSeeMore,
  collapse: preliquidationSelector(state).collapseTable,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  toggleItem: (item: PreliquidationItem) => {
    dispatch(preliActions.toggleItem(item));
  },
  toggleAll: () => {
    dispatch(preliActions.toggleAll());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(TablePreliquidation);
